import { Footer } from '@components/footer';
import { SvgSources } from '@components/icons/svgSources';
import { Navbar } from '@components/navbar';
import { Toaster } from '@components/ui/toast/toaster';
import { UploadQueue } from '@components/uploadQueue';
import { useAppContext } from '@contexts/app';
import { DropdownsProvider } from '@contexts/dropdowns';
import { cn } from '@utils/cn';
import { appRoutes, foremanRoute } from '@utils/consts';
import { handleLanguageChange } from '@utils/i18n';
import { useEffect, useMemo } from 'react';
import { Location, Outlet, useLocation, useParams } from 'react-router';
import { AppScrollRestoration } from '@components/scrollRestoration';
import { APP_SCROLL_CLASS, APP_SUBHEADER_SLOT_ID } from '@hooks/appScroll/constants';
import { useRefreshChannel } from '@hooks/refreshChannel';
import { useBottomNavEnabled } from '@hooks/bottomNav';

const getScrollKey = (location: Location) =>
  location.pathname.includes(foremanRoute.index) ? location.pathname : location.key;

export const Layout = () => {
  const { isLogged: isUserLogged, admin, pushEnabled } = useAppContext();
  const { pathname } = useLocation();
  const { lang } = useParams();
  const isLogged = isUserLogged || admin?.isLogged;

  const notFound = pathname.includes(appRoutes.notFound);
  const bottomNav = useBottomNavEnabled();

  useRefreshChannel();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleLanguageChange(lang || 'en');
  }, [lang]);

  const outletKey = useMemo(() => (pushEnabled ? new Date().toISOString() : ''), [pushEnabled]);

  return (
    <DropdownsProvider>
      <div
        className={cn(
          'flex min-h-screen w-full flex-col min-[900px]:h-dv min-[900px]:min-h-0 min-[900px]:overflow-hidden',
          isLogged && !notFound ? '' : 'login-page',
          bottomNav && 'has-bottom-nav',
        )}>
        <SvgSources />
        <Navbar />
        <div id={APP_SUBHEADER_SLOT_ID} className="contents" />
        {/* <Tutorial /> */}
        <div
          className={cn(
            APP_SCROLL_CLASS,
            'scrollbar-md flex grow flex-col min-[900px]:min-h-0 min-[900px]:overflow-y-auto min-[900px]:[scrollbar-gutter:stable]',
            bottomNav &&
              'pb-[calc(65px+env(safe-area-inset-bottom,0px))] [.workload-schedule-peek_&]:pb-[calc(124px+env(safe-area-inset-bottom,0px))]',
          )}>
          <main
            key={outletKey}
            className={cn(
              'main mx-auto w-full pb-5 p-5 max-[899px]:[.job-page_&]:px-0 max-[899px]:[.job-page_&]:pt-2 max-[899px]:[.phone-page_&]:px-3 max-[899px]:[.map-page_&]:!p-0',
              isLogged && !notFound ? 'grow' : 'pt-10',
            )}>
            <Outlet />
          </main>
          {!bottomNav && <Footer isCompact={!isLogged || notFound} />}
        </div>
        <AppScrollRestoration getKey={getScrollKey} />
        <Toaster />
        <UploadQueue />
      </div>
    </DropdownsProvider>
  );
};
